import { ChangeEvent, useRef, useState } from 'react';
import { uploadSiteFavicon } from '../../../api/platform';
import { Modal, SectionLabel } from '../../../components/fs';
import { PHONE, useMediaQuery } from '../../../shared/media';
import {
  ACCENTS, SiteDoc, SiteFont, SiteRadius, SiteShot,
} from '../../../shared/siteContent';
import { ago } from '../../admin/SsoShared';
import SitePreview from './SitePreview';
import type { SiteDraft } from './useSiteDraft';

/**
 * Site content › Site (the handoff's tab 1): the Look, then the Page — Home
 * or Features — and beside them the preview of the draft. Everything here is
 * the shared draft: it saves as it is typed and goes live only with Publish
 * (the bar the page draws). Without the Super Admin role every control is
 * disabled and the preview and the pickers still open, to be read.
 */

type Page = 'home' | 'features';

/** Where the picker was opened from: the hero, or a Features row by index. */
type PickTarget = { kind: 'hero' } | { kind: 'row'; index: number };

export default function SiteTab({ draft, shots, readOnly }: {
  draft: SiteDraft;
  shots: SiteShot[];
  readOnly: boolean;
}) {
  const phone = useMediaQuery(PHONE);
  const [page, setPage] = useState<Page>('home');
  const [openRow, setOpenRow] = useState(0);
  const [picking, setPicking] = useState<PickTarget | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const doc = draft.doc;
  if (!doc) return null;

  const shotOf = (id: string): SiteShot | null => shots.find((s) => s.id === id) || null;
  const path = page === 'home' ? '/' : '/features';

  return (
    <div className="sc-site">
      <div className="sc-site-editor">
        <LookCard draft={draft} doc={doc} readOnly={readOnly} />

        <section className="fs-card sc-pagecard" aria-label="Page">
          <div className="sc-card-head">
            <h2>Page</h2>
            <span className="sc-card-head-gap" />
            {phone && (
              <button type="button" className="fs-btn neutral sm" onClick={() => setPreviewing(true)}>Preview</button>
            )}
            <div className="fs-seg" role="group" aria-label="Page">
              <button type="button" className={page === 'home' ? 'on' : ''} aria-pressed={page === 'home'} onClick={() => setPage('home')}>Home</button>
              <button type="button" className={page === 'features' ? 'on' : ''} aria-pressed={page === 'features'} onClick={() => setPage('features')}>Features</button>
            </div>
          </div>
          {page === 'home' ? (
            <HomeFields doc={doc} draft={draft} readOnly={readOnly} hero={shotOf(doc.home.hero)} onPick={() => setPicking({ kind: 'hero' })} />
          ) : (
            <FeaturesFields
              doc={doc} draft={draft} readOnly={readOnly} shotOf={shotOf}
              openRow={openRow} onOpenRow={setOpenRow} onPick={(index) => setPicking({ kind: 'row', index })}
            />
          )}
        </section>
      </div>

      {!phone && (
        <section className="fs-card sc-preview" aria-label="Preview">
          <PreviewHead draft={draft} page={page} />
          <SitePreview path={path} doc={doc} shots={shots} />
        </section>
      )}

      {phone && previewing && (
        <Modal title="Preview" wide onClose={() => setPreviewing(false)} className="sc-preview-sheet">
          <PreviewHead draft={draft} page={page} titled={false} />
          <SitePreview path={path} doc={doc} shots={shots} native />
        </Modal>
      )}

      {picking && (
        <ShotPicker
          shots={shots}
          where={picking.kind === 'hero' ? 'Home · hero' : `Features · ${doc.features.rows[picking.index].short || doc.features.rows[picking.index].title}`}
          current={picking.kind === 'hero' ? doc.home.hero : doc.features.rows[picking.index].shot}
          readOnly={readOnly}
          onClose={() => setPicking(null)}
          onPick={(id) => {
            const target = picking;
            draft.edit((d) => {
              if (target.kind === 'hero') d.home.hero = id;
              else d.features.rows[target.index].shot = id;
            });
            setPicking(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Look
// ---------------------------------------------------------------------------

function LookCard({ draft, doc, readOnly }: { draft: SiteDraft; doc: SiteDoc; readOnly: boolean }) {
  const look = doc.look;
  const seg = <T extends string>(value: T, options: [T, string][], set: (v: T) => void, label: string) => (
    <div className="fs-seg" role="group" aria-label={label}>
      {options.map(([v, l]) => (
        <button
          key={v} type="button" className={value === v ? 'on' : ''} aria-pressed={value === v}
          disabled={readOnly} onClick={() => set(v)}
        >
          {l}
        </button>
      ))}
    </div>
  );
  return (
    <section className="fs-card sc-look" aria-label="Look">
      <div className="sc-card-head">
        <h2>Look</h2>
        <span className="sc-card-aside">Every public page</span>
      </div>
      <div className="sc-look-row">
        <span className="sc-look-label" id="sc-accent-label">Accent</span>
        <div className="sc-swatches" role="group" aria-labelledby="sc-accent-label">
          {ACCENTS.map((a) => (
            <button
              key={a.key} type="button" aria-pressed={look.accent === a.key}
              className={`sc-swatch${look.accent === a.key ? ' on' : ''}`} data-accent={a.key}
              title={a.label} aria-label={a.label} disabled={readOnly}
              onClick={() => draft.edit((d) => { d.look.accent = a.key; })}
            />
          ))}
        </div>
      </div>
      <div className="sc-look-row">
        <span className="sc-look-label">Headings</span>
        {seg<SiteFont>(look.font, [['sans', 'Sans · Geist'], ['serif', 'Serif · Newsreader']],
          (v) => draft.edit((d) => { d.look.font = v; }), 'Headings')}
      </div>
      <div className="sc-look-row">
        <span className="sc-look-label">Corners</span>
        {seg<SiteRadius>(look.radius, [['round', 'Rounded'], ['square', 'Square']],
          (v) => draft.edit((d) => { d.look.radius = v; }), 'Corners')}
      </div>
      <FaviconRow draft={draft} name={look.favicon} readOnly={readOnly} />
    </section>
  );
}

const FAVICON_TYPES = 'image/png,image/x-icon,image/vnd.microsoft.icon,.ico,.png';
/** An icon's picture: an upload by its name, the built-in FM mark by its own (server/src/routes/siteFiles). */
const faviconSrc = (name: string | null) => `/site-files/${name || 'favicon-builtin.png'}`;

/**
 * The browser-tab icon — part of the Look, so it waits for Publish like the
 * rest of it. Uploading stores the file (PNG or ICO, checked by the server)
 * and puts its name in the draft; "Use the built-in icon" takes it out again.
 */
function FaviconRow({ draft, name, readOnly }: { draft: SiteDraft; name: string | null; readOnly: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const facts = name ? draft.favicons[name] : null;

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const r = await uploadSiteFavicon(file);
      draft.noteFavicon(r.favicon.name, { width: r.favicon.width, height: r.favicon.height, type: r.favicon.type });
      draft.edit((d) => { d.look.favicon = r.favicon.name; });
    } catch (err) {
      setError((err as Error).message || 'The icon could not be uploaded.');
    } finally {
      setBusy(false);
    }
  };

  const warning = !facts ? ''
    : facts.width !== facts.height
      ? `This icon is ${facts.width}×${facts.height}, not square, so a browser will squash it.`
      : facts.width < 32
        ? `Only ${facts.width}px. A tab on a sharp screen draws 32px, so it will look soft — 180px or more stays sharp everywhere.`
        : '';

  return (
    <div className="sc-look-row sc-favicon-row">
      <span className="sc-look-label">Favicon</span>
      <div className="sc-favicon">
        <span className="sc-favicon-tile" aria-hidden="true">
          <img src={faviconSrc(name)} alt="" width={32} height={32} />
        </span>
        <span className="sc-favicon-text">
          <span className="sc-favicon-name">
            {name ? (facts ? `Your icon · ${facts.width}×${facts.height} ${facts.type === 'image/x-icon' ? 'ICO' : 'PNG'}` : 'Your icon') : 'The built-in FM mark'}
          </span>
          <span className="sc-favicon-hint">The browser-tab icon on every page of the site and the app. PNG or ICO, square.</span>
        </span>
        {/* Upload buttons are the Super Admin's alone, like "+ Upload screenshot" (the handoff's read-only rules). */}
        {!readOnly && (
          <span className="sc-favicon-actions">
            <button type="button" className="fs-btn neutral sm" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? 'Uploading…' : name ? 'Replace…' : 'Upload…'}
            </button>
            {name && (
              <button type="button" className="fs-btn text sm" disabled={busy} onClick={() => draft.edit((d) => { d.look.favicon = null; })}>
                Use the built-in icon
              </button>
            )}
          </span>
        )}
        {!readOnly && <input ref={input} type="file" accept={FAVICON_TYPES} hidden onChange={onFile} aria-label="Favicon file" />}
      </div>
      {warning && <div className="sc-note warn">{warning}</div>}
      {error && <div className="ad-err sc-row-err">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page: Home and Features
// ---------------------------------------------------------------------------

function Field({ id, label, value, onChange, readOnly, mono, multiline, rows = 3, placeholder }: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  mono?: boolean;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div className="fs-field">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea
          id={id} rows={rows} value={value} disabled={readOnly} placeholder={placeholder}
          className={mono ? 'sc-mono' : undefined} onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id} type="text" value={value} disabled={readOnly} placeholder={placeholder}
          className={mono ? 'sc-mono' : undefined} onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

/** A chosen screenshot: its thumbnail, file name and alt text, and "Change…". */
function ShotRow({ shot, fallbackId, readOnly, onPick }: {
  shot: SiteShot | null;
  fallbackId: string;
  readOnly: boolean;
  onPick: () => void;
}) {
  return (
    <div className="sc-shotrow">
      <span className="sc-thumb">{shot && <img src={shot.desktop.src} alt="" loading="lazy" />}</span>
      <span className="sc-shotrow-text">
        <span className="sc-mono">{shot?.file || `${fallbackId}.png`}</span>
        <span className="sc-shotrow-alt">{shot?.alt || 'Not in the library'}</span>
      </span>
      <button type="button" className="fs-btn text sm" disabled={readOnly} onClick={onPick}>Change…</button>
    </div>
  );
}

function HomeFields({ doc, draft, readOnly, hero, onPick }: {
  doc: SiteDoc;
  draft: SiteDraft;
  readOnly: boolean;
  hero: SiteShot | null;
  onPick: () => void;
}) {
  const h = doc.home;
  const set = (key: 'eyebrow' | 'eyebrowLink' | 'h1' | 'lede' | 'cta1' | 'cta2') =>
    (v: string) => draft.edit((d) => { d.home[key] = v; });
  return (
    <div className="sc-fields">
      <SectionLabel>Announcement</SectionLabel>
      <div className="sc-grid sc-grid-announce">
        <Field id="sc-home-eyebrow" label="Text" value={h.eyebrow} onChange={set('eyebrow')} readOnly={readOnly} />
        <Field id="sc-home-eyebrow-link" label="Links to" value={h.eyebrowLink} onChange={set('eyebrowLink')} readOnly={readOnly} mono />
      </div>
      <SectionLabel>Hero</SectionLabel>
      <Field id="sc-home-h1" label="Headline" value={h.h1} onChange={set('h1')} readOnly={readOnly} />
      <Field id="sc-home-lede" label="Intro" value={h.lede} onChange={set('lede')} readOnly={readOnly} multiline />
      <div className="sc-grid sc-grid-cta">
        <Field id="sc-home-cta1" label="Main button" value={h.cta1} onChange={set('cta1')} readOnly={readOnly} />
        <Field id="sc-home-cta2" label="Second button" value={h.cta2} onChange={set('cta2')} readOnly={readOnly} />
      </div>
      <div className="sc-field">
        <span className="sc-field-label">Screenshot</span>
        <ShotRow shot={hero} fallbackId={h.hero} readOnly={readOnly} onPick={onPick} />
      </div>
      <SectionLabel>Three pillars</SectionLabel>
      {h.pillars.map((p, i) => (
        <div className="sc-pillar" key={i}>
          <span className="sc-index">{String(i + 1).padStart(2, '0')}</span>
          <div className="sc-pillar-fields">
            <div className="sc-grid sc-grid-pillar">
              <input
                type="text" aria-label={`Pillar ${i + 1} label`} className="fs-input sc-mono" value={p.k} disabled={readOnly}
                onChange={(e) => { const v = e.target.value; draft.edit((d) => { d.home.pillars[i].k = v; }); }}
              />
              <input
                type="text" aria-label={`Pillar ${i + 1} title`} className="fs-input sc-strong" value={p.title} disabled={readOnly}
                onChange={(e) => { const v = e.target.value; draft.edit((d) => { d.home.pillars[i].title = v; }); }}
              />
            </div>
            <textarea
              aria-label={`Pillar ${i + 1} text`} className="fs-input" rows={2} value={p.body} disabled={readOnly}
              onChange={(e) => { const v = e.target.value; draft.edit((d) => { d.home.pillars[i].body = v; }); }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function FeaturesFields({ doc, draft, readOnly, shotOf, openRow, onOpenRow, onPick }: {
  doc: SiteDoc;
  draft: SiteDraft;
  readOnly: boolean;
  shotOf: (id: string) => SiteShot | null;
  openRow: number;
  onOpenRow: (i: number) => void;
  onPick: (index: number) => void;
}) {
  const f = doc.features;
  return (
    <div className="sc-fields">
      <Field id="sc-feat-h1" label="Headline" value={f.h1} onChange={(v) => draft.edit((d) => { d.features.h1 = v; })} readOnly={readOnly} />
      <Field id="sc-feat-sub" label="Intro" value={f.sub} onChange={(v) => draft.edit((d) => { d.features.sub = v; })} readOnly={readOnly} multiline />
      <SectionLabel>Sections · in page order</SectionLabel>
      <div className="sc-rowlist">
        {f.rows.map((r, i) => {
          const open = openRow === i;
          const shot = shotOf(r.shot);
          const setRow = (key: 'title' | 'short' | 'lede') =>
            (v: string) => draft.edit((d) => { d.features.rows[i][key] = v; });
          return (
            <div className={`sc-rowitem${open ? ' open' : ''}`} key={r.id}>
              <button
                type="button" className="sc-rowitem-head" aria-expanded={open} aria-controls={`sc-row-${r.id}`}
                onClick={() => onOpenRow(open ? -1 : i)}
              >
                <span className="sc-index">{String(i + 1).padStart(2, '0')}</span>
                <span className="sc-rowitem-title">
                  <b>{r.title || r.id}</b>
                  <span>Index: {r.short}</span>
                </span>
                <span className="sc-rowitem-file sc-mono">{shot?.file || `${r.shot}.png`}</span>
                <span className="sc-rowitem-sign" aria-hidden="true">{open ? '−' : '+'}</span>
              </button>
              {open && (
                <div className="sc-rowitem-body" id={`sc-row-${r.id}`}>
                  <div className="sc-grid sc-grid-title">
                    <Field id={`sc-row-${r.id}-title`} label="Title" value={r.title} onChange={setRow('title')} readOnly={readOnly} />
                    <Field id={`sc-row-${r.id}-short`} label="In the index" value={r.short} onChange={setRow('short')} readOnly={readOnly} />
                  </div>
                  <Field id={`sc-row-${r.id}-lede`} label="Intro" value={r.lede} onChange={setRow('lede')} readOnly={readOnly} multiline />
                  <Field
                    id={`sc-row-${r.id}-points`} label="Bullets · one per line" value={r.points.join('\n')} readOnly={readOnly}
                    multiline rows={5}
                    onChange={(v) => draft.edit((d) => { d.features.rows[i].points = v.split('\n'); })}
                  />
                  <ShotRow shot={shot} fallbackId={r.shot} readOnly={readOnly} onPick={() => onPick(i)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview head and the screenshot picker
// ---------------------------------------------------------------------------

function PreviewHead({ draft, page, titled = true }: { draft: SiteDraft; page: Page; titled?: boolean }) {
  const dirty = draft.changes.length > 0;
  const who = draft.draftAt ? `Draft by ${draft.draftBy || 'a Super Admin'} · ${ago(draft.draftAt) || 'just now'}` : undefined;
  const favicon = draft.doc?.look.favicon;
  const host = typeof window !== 'undefined' ? window.location.host : 'form-magic.com';
  return (
    <div className="sc-preview-head">
      {titled && <b>Preview</b>}
      <span className={`fs-pill ${dirty ? 'warn' : 'ok'}`} title={dirty ? who : undefined}>
        {dirty ? 'Draft · not live yet' : 'Same as the live site'}
      </span>
      <span className="sc-card-head-gap" />
      <span className="sc-preview-url">
        {/* the tab's icon, as a browser draws it beside the address */}
        <img src={faviconSrc(favicon ?? null)} alt="" width={14} height={14} />
        {page === 'home' ? host : `${host}/features`}
      </span>
    </div>
  );
}

/** "Choose a screenshot" (720px): the library as tiles; the one in use is tagged. */
function ShotPicker({ shots, where, current, readOnly, onClose, onPick }: {
  shots: SiteShot[];
  where: string;
  current: string;
  readOnly: boolean;
  onClose: () => void;
  onPick: (id: string) => void;
}) {
  const [sel, setSel] = useState(current);
  return (
    <Modal
      title="Choose a screenshot"
      sub={`For ${where}. Upload new ones on the Screenshots tab.`}
      className="sc-picker"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="fs-btn primary" disabled={readOnly || sel === current} onClick={() => onPick(sel)}>
            Use this screenshot
          </button>
          <button type="button" className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="sc-picker-grid" role="group" aria-label="Screenshots">
        {shots.map((s) => (
          <button
            key={s.id} type="button" aria-pressed={sel === s.id}
            className={`sc-pick${sel === s.id ? ' on' : ''}`} onClick={() => setSel(s.id)}
          >
            <span className="sc-pick-img"><img src={s.desktop.src} alt="" loading="lazy" /></span>
            <span className="sc-pick-name">
              <span className="sc-mono">{s.file}</span>
              {s.id === current && <span className="sc-inuse">In use</span>}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
